import { FC, SyntheticEvent, useEffect, useRef, useState } from 'react';
import { Divider, Stack, Typography } from '@mui/material';
import { LoadingButton, TreeItem, TreeView } from '@mui/lab';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Icon } from '@iconify/react';
import TechImpact from '@models/TechImpact';
import Router from 'next/router';
import { StatsApi } from '@api/technologies/stats';
import {
  capitalizeFirstLetter,
  rewriteRouteWithNewCriteria,
} from '../../../../shared/functions';
import { getImpactIconName } from '../../ImpactIndicator/functions';

interface TechExtendedImpactIndicatorProps {
  keyword: string;
  techImpacts: TechImpact[];
}

export const TechExtendedImpactIndicator: FC<TechExtendedImpactIndicatorProps> =
  ({ keyword, techImpacts }) => {
    const ref = useRef();

    const [expandedItems, setExpandedItems] = useState<string[]>([]);

    const [actualImpacts, setActualImpacts] = useState<TechImpact[]>([]);

    useEffect(() => {
      setActualImpacts(techImpacts);
    }, [techImpacts]);

    useEffect(() => {
      function getExpandedItems() {
        if (actualImpacts && actualImpacts.length > 0) {
          const expandedAffiliations = [actualImpacts[0].impactLevel];
          actualImpacts?.forEach((impact) => {
            impact?.impactAffiliations?.forEach((affiliation) => {
              affiliation?.relatedAffiliations?.forEach(() => {
                expandedAffiliations.push(
                  `node${affiliation.literalName + impact.impactLevel}`,
                );
                expandedAffiliations.push(
                  `related-affiliation-${
                    affiliation.literalName + impact.impactLevel
                  }`,
                );
              });
            });
          });

          return expandedAffiliations;
        }

        return [''];
      }

      if (actualImpacts && actualImpacts.length > 0) {
        setExpandedItems(getExpandedItems());
      } else {
        setExpandedItems([]);
      }
    }, [actualImpacts]);

    const handleToggle = (event: SyntheticEvent, nodeIds: string[]) => {
      setExpandedItems(nodeIds);
    };

    const [nonExistentAffiliations, setNonExistentAffiliations] = useState<
      string[]
    >([]);

    const [existentAffiliations, setExistentAffiliations] = useState<string[]>(
      [],
    );

    const isNonExistentAffiliation = (affiliation: string) =>
      nonExistentAffiliations.includes(affiliation);

    const [loading, setLoading] = useState<string>();

    return (
      <Stack
        spacing={3}
        sx={{ px: 2, maxWidth: 400 }}
        justifyContent={'center'}
        alignItems={'center'}
      >
        <Stack direction={'row'} spacing={1} alignItems={'center'}>
          <Typography variant={'h6'}>Related Technology Impacts</Typography>
        </Stack>
        <Divider variant={'middle'} sx={{ width: '100%' }} />
        <TreeView
          ref={ref}
          expanded={expandedItems}
          defaultCollapseIcon={<ExpandMoreIcon />}
          defaultExpandIcon={<ChevronRightIcon />}
          onNodeToggle={handleToggle}
        >
          {actualImpacts.map((impact) => (
            <TreeItem
              nodeId={impact?.impactLevel}
              key={impact?.impactLevel}
              label={
                <Stack direction={'row'} alignItems={'center'} spacing={0.5}>
                  <Typography
                    sx={{
                      p: 0,
                      fontWeight: expandedItems.includes(impact.impactLevel)
                        ? 700
                        : 400,
                    }}
                  >
                    {capitalizeFirstLetter(impact.impactLevel)}
                  </Typography>
                  <Icon
                    fontSize={20}
                    icon={getImpactIconName(impact?.impactLevel, 'square')}
                  />
                </Stack>
              }
            >
              {impact?.impactAffiliations?.map((affiliation) => (
                <TreeItem
                  nodeId={`node${affiliation.literalName + impact.impactLevel}`}
                  key={affiliation.literalName + impact.impactLevel}
                  icon={
                    isNonExistentAffiliation(affiliation.literalName) ||
                    existentAffiliations.includes(affiliation.literalName) ? (
                      <Icon
                        icon={'fluent:organization-horizontal-20-filled'}
                        color={
                          isNonExistentAffiliation(affiliation.literalName)
                            ? 'red'
                            : 'green'
                        }
                      />
                    ) : null
                  }
                  label={
                    <Stack direction={'row'} spacing={1}>
                      <Typography
                        sx={{ p: 0, color: '#163d72' }}
                      >{`${affiliation.literalName}`}</Typography>
                    </Stack>
                  }
                >
                  {!affiliation?.relatedAffiliations ||
                  affiliation?.relatedAffiliations?.length === 0 ? (
                    <LoadingButton
                      endIcon={
                        <Icon
                          icon={
                            isNonExistentAffiliation(affiliation.literalName)
                              ? ''
                              : 'fluent:organization-horizontal-20-regular'
                          }
                        />
                      }
                      sx={{
                        width: '100%',
                        my: 1,
                        color: isNonExistentAffiliation(affiliation.literalName)
                          ? 'white'
                          : 'black',
                        backgroundColor: isNonExistentAffiliation(
                          affiliation.literalName,
                        )
                          ? '#3a3535'
                          : null,
                        '&:hover': {
                          backgroundColor: isNonExistentAffiliation(
                            affiliation.literalName,
                          )
                            ? 'black'
                            : null,
                        },
                      }}
                      loading={loading === affiliation.literalName}
                      variant={'outlined'}
                      onClick={() => {
                        setLoading(affiliation.literalName);
                        StatsApi.getTechAffiliations(
                          affiliation.literalName,
                          keyword,
                        ).then((res) => {
                          setLoading(null);
                          if (res.data && res.data.length === 0) {
                            setNonExistentAffiliations([
                              ...nonExistentAffiliations,
                              affiliation.literalName,
                            ]);
                          } else {
                            setExistentAffiliations([
                              ...existentAffiliations,
                              affiliation.literalName,
                            ]);
                            setActualImpacts(
                              actualImpacts.map((impact) => ({
                                impactLevel: impact.impactLevel,
                                impactAffiliations:
                                  impact.impactAffiliations.map(
                                    (affiliation2) => ({
                                      literalName: affiliation2.literalName,
                                      relatedAffiliations:
                                        affiliation2.literalName ===
                                        affiliation.literalName
                                          ? res.data
                                          : affiliation2.relatedAffiliations,
                                    }),
                                  ),
                              })),
                            );
                          }
                        });
                      }}
                    >
                      {isNonExistentAffiliation(affiliation.literalName)
                        ? 'No Related Affiliations'
                        : 'Search Affiliations'}
                    </LoadingButton>
                  ) : (
                    affiliation.relatedAffiliations.map((affiliation) => (
                      <TreeItem
                        key={`related-affiliation-${
                          affiliation + impact.impactLevel
                        }`}
                        nodeId={`related-affiliation-${
                          affiliation + impact.impactLevel
                        }`}
                        label={<Typography>{affiliation}</Typography>}
                        onClick={() => {
                          rewriteRouteWithNewCriteria(Router, {
                            q: affiliation,
                            field: 'affiliations',
                            accuracy: 'Vague',
                            operator: 'AND',
                          });
                        }}
                      />
                    ))
                  )}
                </TreeItem>
              ))}
            </TreeItem>
          ))}
        </TreeView>
        <Divider variant={'middle'} sx={{ width: '100%' }} />
      </Stack>
    );
  };
